import { Howl } from 'howler';
import { debounce, DebouncedFunc } from 'lodash';
import unitTimersAlert from '../assets/sounds/unit_timers_alert.mp3';
import aedAttemptingAccepting from '../assets/sounds/aed_attempting_accepting.mp3';
import dexHotHitAlert from '../assets/sounds/dex_hot_hit_notification.mp3';
import newMessageReceived from '../assets/sounds/msg_ding.mp3';
import firstResponderIncomingMsg from '../assets/sounds/beep.mp3';
import emergencySiren from '../assets/sounds/emergency_siren.mp3';
import incomingBolo from '../assets/sounds/incoming_bolo_alert.mp3';
import draftEventTimersAlert from '../assets/sounds/draft_event_timers_alert.mp3'

export type SoundName =
    | 'unit-timer-alert'
    | 'aed-attempting-accepting'
    | 'dex-hot-hit-alert'
    | 'new-message-received'
    | 'first-responder-incoming-msg'
    | 'first-responder-incoming-bolo'
    | 'emergency-siren'
    | 'draft-event-timer-alert';

const soundMap: Record<SoundName, InstanceType<typeof Howl>> = {
    'unit-timer-alert': new Howl({ src: [unitTimersAlert] }),
    'aed-attempting-accepting': new Howl({ src: [aedAttemptingAccepting] }),
    'dex-hot-hit-alert': new Howl({ src: [dexHotHitAlert] }),
    'new-message-received': new Howl({ src: [newMessageReceived] }),
    'first-responder-incoming-msg': new Howl({ src: [firstResponderIncomingMsg] }),
    'first-responder-incoming-bolo': new Howl({ src: [incomingBolo] }),
    'emergency-siren': new Howl({ src: [emergencySiren] }),
    'draft-event-timer-alert': new Howl({ src: [draftEventTimersAlert] }),
};

type PlayMode = 'standard' | 'looping';

export type PlaySoundOptions = {
    mode?: PlayMode;
    skipIfPlaying?: boolean;
};

type DebouncedPlay = DebouncedFunc<(opts?: PlaySoundOptions) => void>;

const resolveSkipIfPlaying = (opts?: PlaySoundOptions) => opts?.skipIfPlaying ?? true;

const doPlay = (soundName: SoundName, opts?: PlaySoundOptions) => {
    const howl = soundMap[soundName];
    const mode: PlayMode = opts?.mode ?? 'standard';

    howl.loop(mode === 'looping');

    if (resolveSkipIfPlaying(opts) && howl.playing()) return;

    howl.play();
};

const soundNames = Object.keys(soundMap) as SoundName[];

const debouncedPlayBySound: Record<SoundName, DebouncedPlay> = Object.fromEntries(
    soundNames.map((soundName) => [
        soundName,
        debounce(
            (opts?: PlaySoundOptions) => doPlay(soundName, opts),
            1000,
            { leading: true, trailing: false }
        ),
    ])
) as Record<SoundName, DebouncedPlay>;

export function playSound(soundName: SoundName, options?: PlaySoundOptions): void {
    const mode: PlayMode = options?.mode ?? 'standard';

    if (mode === 'looping') {
        doPlay(soundName, options);
        return;
    }

    debouncedPlayBySound[soundName](options);
}

export const stopSound = (soundName: SoundName): void => {
    debouncedPlayBySound[soundName]?.cancel();

    const howl = soundMap[soundName];
    howl.loop(false);

    if (!howl.playing()) return;

    howl.stop();
};
